import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useTenantContext } from '@/hooks/useTenantContext';
import { apiClient } from '@/api/client';
import { TenantListItem } from '@/types';

export function TenantSwitcher() {
  const { t } = useTranslation();
  const { isSuperAdmin, impersonatedTenantId } = useTenantContext();
  const { setImpersonatedTenant } = useAuthStore();
  const [tenants, setTenants] = useState<TenantListItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      apiClient
        .get<TenantListItem[]>('/admin/tenants')
        .then((res) => setTenants(res.data))
        .catch(() => {});
    }
  }, [isSuperAdmin]);

  if (!isSuperAdmin) return null;

  const selectedTenant = tenants.find((t) => t.id === impersonatedTenantId);

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg border bg-background hover:bg-accent text-sm"
      >
        <span className="text-muted-foreground">{t('layout.tenantSwitcher.tenant')}</span>
        <span className="font-medium">
          {selectedTenant?.name || t('layout.tenantSwitcher.allSuperAdmin')}
        </span>
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute top-full start-0 mt-1 w-64 bg-popover border rounded-lg shadow-lg z-50 max-h-80 overflow-y-auto">
            <button
              onClick={() => {
                setImpersonatedTenant(null);
                setIsOpen(false);
              }}
              className="w-full text-start px-4 py-3 hover:bg-accent text-sm border-b"
            >
              {t('layout.tenantSwitcher.allSuperAdmin')}
            </button>
            {tenants.map((tenant) => (
              <button
                key={tenant.id}
                onClick={() => {
                  setImpersonatedTenant(tenant.id, tenant.name);
                  setIsOpen(false);
                }}
                className={`w-full text-start px-4 py-3 hover:bg-accent text-sm flex items-center justify-between ${
                  impersonatedTenantId === tenant.id ? 'bg-accent' : ''
                }`}
              >
                <span>{tenant.name}</span>
                <span className="text-xs text-muted-foreground">{tenant.identifier}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
